import { useState, useMemo, useCallback } from 'react';
import { useForm, Link, router } from '@inertiajs/react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/Layouts/AppLayout';

// Room type configurations
const ROOM_TYPES = [
    { key: 'single', label: 'Single', icon: 'bi-person', desc: '1 Adult', color: '#6366f1' },
    { key: 'double', label: 'Double', icon: 'bi-people', desc: '2 Adults', color: '#8b5cf6' },
    { key: 'triple', label: 'Triple', icon: 'bi-people-fill', desc: '3 Adults', color: '#a855f7' },
    { key: 'quad', label: 'Quad', icon: 'bi-grid-fill', desc: '4 Adults', color: '#d946ef' },
    { key: 'extra_bed', label: 'Extra Bed', icon: 'bi-plus-square', desc: '+1 Bed', color: '#ec4899' },
    { key: 'cwb', label: 'CWB', icon: 'bi-person-arms-up', desc: 'Child With Bed', color: '#f43f5e' },
    { key: 'cnb', label: 'CNB', icon: 'bi-person-dash', desc: 'Child No Bed', color: '#f97316' },
    { key: 'infant', label: 'Infant', icon: 'bi-emoji-smile', desc: '0-2 years', color: '#eab308' },
];

export default function Create({ query, currencies, markupTypes }) {
    const [step, setStep] = useState(1);
    const [showPreview, setShowPreview] = useState(false);

    const { data, setData, post, processing, errors } = useForm({
        title: `${query.destinations?.map(d => d.name).join(' + ') || 'Custom'} Package`,
        currency_id: query.currency_id || currencies.find(c => c.code === 'INR')?.id || '',
        currency_rate: 1,
        markup_type: 'percentage',
        markup_value: 10,
        travel_date: query.travel_date || '',
        return_date: query.return_date || '',
        nights: query.nights || 0,
        adults: query.adults || 2,
        children: query.children || 0,
        infants: query.infants || 0,
        room_config: {
            single: 0,
            double: Math.ceil((query.adults || 2) / 2),
            triple: 0,
            quad: 0,
            extra_bed: 0,
            cwb: query.children || 0,
            cnb: 0,
            infant: query.infants || 0,
        },
        inclusions: '',
        exclusions: '',
        payment_terms: '',
        cancellation_policy: '',
        notes: '',
    });

    // Calculate nights from dates
    const calculateNights = useCallback((startDate, endDate) => {
        if (!startDate || !endDate) return 0;
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = end - start;
        return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
    }, []);

    // Handle date changes
    const handleDateChange = (field, value) => {
        const newData = { ...data, [field]: value };
        if (field === 'travel_date' && data.return_date) {
            newData.nights = calculateNights(value, data.return_date);
        } else if (field === 'return_date' && data.travel_date) {
            newData.nights = calculateNights(data.travel_date, value);
        } else if (field === 'nights' && data.travel_date) {
            const start = new Date(data.travel_date);
            start.setDate(start.getDate() + parseInt(value) || 0);
            newData.return_date = start.toISOString().split('T')[0];
        }
        setData(newData);
    };

    // Handle room config changes
    const handleRoomChange = (type, value) => {
        setData('room_config', {
            ...data.room_config,
            [type]: Math.max(0, parseInt(value) || 0),
        });
    };

    // Calculate total pax from room config
    const totalPaxFromRooms = useMemo(() => {
        const rc = data.room_config;
        return {
            adults: (rc.single || 0) + (rc.double || 0) * 2 + (rc.triple || 0) * 3 + (rc.quad || 0) * 4 + (rc.extra_bed || 0),
            children: (rc.cwb || 0) + (rc.cnb || 0),
            infants: rc.infant || 0,
        };
    }, [data.room_config]);

    // Sync pax with room config
    const syncPaxWithRooms = () => {
        setData(prev => ({
            ...prev,
            adults: totalPaxFromRooms.adults,
            children: totalPaxFromRooms.children,
            infants: totalPaxFromRooms.infants,
        }));
    };

    // Auto-suggest room config based on pax
    const autoSuggestRooms = () => {
        const adults = data.adults || 0;
        const children = data.children || 0;
        const infants = data.infants || 0;

        // Simple algorithm: pairs of adults in double rooms, odd one in single
        const doubles = Math.floor(adults / 2);
        const singles = adults % 2;

        setData('room_config', {
            ...data.room_config,
            single: singles,
            double: doubles,
            triple: 0,
            quad: 0,
            extra_bed: 0,
            cwb: children,
            cnb: 0,
            infant: infants,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        post(`/queries/${query.id}/quotations`);
    };

    const currency = currencies.find(c => c.id === parseInt(data.currency_id)) || { symbol: '₹', code: 'INR' };

    // Steps configuration
    const steps = [
        { id: 1, title: 'Basic Info', icon: 'bi-info-circle' },
        { id: 2, title: 'Travelers', icon: 'bi-people' },
        { id: 3, title: 'Room Config', icon: 'bi-building' },
        { id: 4, title: 'Settings', icon: 'bi-gear' },
    ];

    return (
        <AppLayout title="Create Quotation">
            <div className="qb-create-page">
                {/* Header */}
                <div className="qb-header">
                    <div className="qb-header-left">
                        <Link href={`/queries/${query.id}`} className="qb-back-btn">
                            <i className="bi bi-arrow-left"></i>
                        </Link>
                        <div>
                            <h1 className="qb-title">New Quotation</h1>
                            <p className="qb-subtitle">
                                {query.query_number} • {query.client?.name || query.guest_name}
                            </p>
                        </div>
                    </div>
                    <div className="qb-header-right">
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2"
                            onClick={() => setShowPreview(!showPreview)}
                        >
                            <i className={`bi bi-${showPreview ? 'pencil' : 'eye'} me-1`}></i>
                            {showPreview ? 'Edit' : 'Preview'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={processing}
                        >
                            {processing ? (
                                <span className="spinner-border spinner-border-sm me-1"></span>
                            ) : (
                                <i className="bi bi-check-lg me-1"></i>
                            )}
                            Create & Build Itinerary
                        </button>
                    </div>
                </div>

                {/* Progress Steps */}
                <div className="qb-steps">
                    {steps.map((s, idx) => (
                        <button
                            key={s.id}
                            className={`qb-step ${step === s.id ? 'active' : ''} ${step > s.id ? 'completed' : ''}`}
                            onClick={() => setStep(s.id)}
                        >
                            <span className="qb-step-number">
                                {step > s.id ? <i className="bi bi-check"></i> : s.id}
                            </span>
                            <span className="qb-step-title">{s.title}</span>
                        </button>
                    ))}
                </div>

                <div className="qb-content">
                    <div className="row g-4">
                        {/* Main Form */}
                        <div className={showPreview ? 'col-lg-7' : 'col-lg-8'}>
                            <form onSubmit={handleSubmit}>
                                <AnimatePresence mode="wait">
                                    {/* Step 1: Basic Info */}
                                    {step === 1 && (
                                        <motion.div
                                            key="step1"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            className="qb-step-content"
                                        >
                                            <div className="qb-card">
                                                <div className="qb-card-header">
                                                    <i className="bi bi-file-earmark-text"></i>
                                                    <h3>Quotation Details</h3>
                                                </div>
                                                <div className="qb-card-body">
                                                    <div className="mb-4">
                                                        <label className="qb-label">Quotation Title</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control form-control-lg ${errors.title ? 'is-invalid' : ''}`}
                                                            value={data.title}
                                                            onChange={e => setData('title', e.target.value)}
                                                            placeholder="e.g., Dubai Family Package - Premium Option"
                                                        />
                                                        {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                                                        <small className="text-muted">
                                                            A descriptive title helps identify this quotation
                                                        </small>
                                                    </div>

                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <label className="qb-label">Currency</label>
                                                            <select
                                                                className="form-select form-select-lg"
                                                                value={data.currency_id}
                                                                onChange={e => setData('currency_id', e.target.value)}
                                                            >
                                                                {currencies.map(c => (
                                                                    <option key={c.id} value={c.id}>
                                                                        {c.code} - {c.symbol} ({c.name})
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="qb-label">Exchange Rate</label>
                                                            <div className="input-group">
                                                                <span className="input-group-text">1 {currency.code} =</span>
                                                                <input
                                                                    type="number"
                                                                    step="0.0001"
                                                                    className="form-control"
                                                                    value={data.currency_rate}
                                                                    onChange={e => setData('currency_rate', e.target.value)}
                                                                />
                                                                <span className="input-group-text">INR</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="qb-card">
                                                <div className="qb-card-header">
                                                    <i className="bi bi-calendar-range"></i>
                                                    <h3>Travel Dates</h3>
                                                </div>
                                                <div className="qb-card-body">
                                                    <div className="row g-3">
                                                        <div className="col-md-4">
                                                            <label className="qb-label">Departure Date</label>
                                                            <input
                                                                type="date"
                                                                className="form-control form-control-lg"
                                                                value={data.travel_date}
                                                                onChange={e => handleDateChange('travel_date', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label className="qb-label">Return Date</label>
                                                            <input
                                                                type="date"
                                                                className="form-control form-control-lg"
                                                                value={data.return_date}
                                                                onChange={e => handleDateChange('return_date', e.target.value)}
                                                                min={data.travel_date}
                                                            />
                                                        </div>
                                                        <div className="col-md-4">
                                                            <label className="qb-label">Duration</label>
                                                            <div className="qb-duration-display">
                                                                <div className="qb-duration-value">
                                                                    <span className="qb-duration-number">{data.nights || 0}</span>
                                                                    <span className="qb-duration-label">Nights</span>
                                                                </div>
                                                                <span className="qb-duration-separator">/</span>
                                                                <div className="qb-duration-value">
                                                                    <span className="qb-duration-number">{(data.nights || 0) + 1}</span>
                                                                    <span className="qb-duration-label">Days</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Quick duration buttons */}
                                                    <div className="mt-3">
                                                        <span className="text-muted small me-2">Quick select:</span>
                                                        {[3, 4, 5, 6, 7, 10, 14].map(n => (
                                                            <button
                                                                key={n}
                                                                type="button"
                                                                className={`btn btn-sm ${data.nights === n ? 'btn-primary' : 'btn-outline-secondary'} me-1 mb-1`}
                                                                onClick={() => handleDateChange('nights', n)}
                                                            >
                                                                {n}N/{n + 1}D
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="qb-step-actions">
                                                <div></div>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-lg"
                                                    onClick={() => setStep(2)}
                                                >
                                                    Next: Travelers
                                                    <i className="bi bi-arrow-right ms-2"></i>
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* Step 2: Travelers */}
                                    {step === 2 && (
                                        <motion.div
                                            key="step2"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            className="qb-step-content"
                                        >
                                            <div className="qb-card">
                                                <div className="qb-card-header">
                                                    <i className="bi bi-people"></i>
                                                    <h3>Number of Travelers</h3>
                                                </div>
                                                <div className="qb-card-body">
                                                    <div className="qb-pax-grid">
                                                        <div className="qb-pax-item">
                                                            <div className="qb-pax-icon" style={{ backgroundColor: '#6366f1' }}>
                                                                <i className="bi bi-person"></i>
                                                            </div>
                                                            <div className="qb-pax-info">
                                                                <label className="qb-pax-label">Adults</label>
                                                                <span className="qb-pax-desc">12+ years</span>
                                                            </div>
                                                            <div className="qb-pax-controls">
                                                                <button
                                                                    type="button"
                                                                    className="qb-pax-btn"
                                                                    onClick={() => setData('adults', Math.max(1, data.adults - 1))}
                                                                >
                                                                    <i className="bi bi-dash"></i>
                                                                </button>
                                                                <span className="qb-pax-value">{data.adults}</span>
                                                                <button
                                                                    type="button"
                                                                    className="qb-pax-btn"
                                                                    onClick={() => setData('adults', data.adults + 1)}
                                                                >
                                                                    <i className="bi bi-plus"></i>
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div className="qb-pax-item">
                                                            <div className="qb-pax-icon" style={{ backgroundColor: '#f97316' }}>
                                                                <i className="bi bi-person-arms-up"></i>
                                                            </div>
                                                            <div className="qb-pax-info">
                                                                <label className="qb-pax-label">Children</label>
                                                                <span className="qb-pax-desc">2-11 years</span>
                                                            </div>
                                                            <div className="qb-pax-controls">
                                                                <button
                                                                    type="button"
                                                                    className="qb-pax-btn"
                                                                    onClick={() => setData('children', Math.max(0, data.children - 1))}
                                                                >
                                                                    <i className="bi bi-dash"></i>
                                                                </button>
                                                                <span className="qb-pax-value">{data.children}</span>
                                                                <button
                                                                    type="button"
                                                                    className="qb-pax-btn"
                                                                    onClick={() => setData('children', data.children + 1)}
                                                                >
                                                                    <i className="bi bi-plus"></i>
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div className="qb-pax-item">
                                                            <div className="qb-pax-icon" style={{ backgroundColor: '#eab308' }}>
                                                                <i className="bi bi-emoji-smile"></i>
                                                            </div>
                                                            <div className="qb-pax-info">
                                                                <label className="qb-pax-label">Infants</label>
                                                                <span className="qb-pax-desc">0-2 years</span>
                                                            </div>
                                                            <div className="qb-pax-controls">
                                                                <button
                                                                    type="button"
                                                                    className="qb-pax-btn"
                                                                    onClick={() => setData('infants', Math.max(0, data.infants - 1))}
                                                                >
                                                                    <i className="bi bi-dash"></i>
                                                                </button>
                                                                <span className="qb-pax-value">{data.infants}</span>
                                                                <button
                                                                    type="button"
                                                                    className="qb-pax-btn"
                                                                    onClick={() => setData('infants', data.infants + 1)}
                                                                >
                                                                    <i className="bi bi-plus"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="qb-pax-summary">
                                                        <span className="qb-pax-total">
                                                            Total: <strong>{data.adults + data.children + data.infants}</strong> travelers
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-primary btn-sm"
                                                            onClick={autoSuggestRooms}
                                                        >
                                                            <i className="bi bi-magic me-1"></i>
                                                            Auto-configure Rooms
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="qb-step-actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-lg"
                                                    onClick={() => setStep(1)}
                                                >
                                                    <i className="bi bi-arrow-left me-2"></i>
                                                    Back
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-lg"
                                                    onClick={() => setStep(3)}
                                                >
                                                    Next: Room Configuration
                                                    <i className="bi bi-arrow-right ms-2"></i>
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* Step 3: Room Configuration */}
                                    {step === 3 && (
                                        <motion.div
                                            key="step3"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            className="qb-step-content"
                                        >
                                            <div className="qb-card">
                                                <div className="qb-card-header">
                                                    <i className="bi bi-building"></i>
                                                    <h3>Room Configuration</h3>
                                                    <span className="qb-card-badge">
                                                        For hotel pricing calculation
                                                    </span>
                                                </div>
                                                <div className="qb-card-body">
                                                    <div className="qb-rooms-grid">
                                                        {ROOM_TYPES.map(room => (
                                                            <div
                                                                key={room.key}
                                                                className={`qb-room-card ${data.room_config[room.key] > 0 ? 'active' : ''}`}
                                                            >
                                                                <div
                                                                    className="qb-room-icon"
                                                                    style={{ backgroundColor: room.color }}
                                                                >
                                                                    <i className={`bi ${room.icon}`}></i>
                                                                </div>
                                                                <div className="qb-room-info">
                                                                    <span className="qb-room-label">{room.label}</span>
                                                                    <span className="qb-room-desc">{room.desc}</span>
                                                                </div>
                                                                <div className="qb-room-controls">
                                                                    <button
                                                                        type="button"
                                                                        className="qb-room-btn"
                                                                        onClick={() => handleRoomChange(room.key, data.room_config[room.key] - 1)}
                                                                    >
                                                                        <i className="bi bi-dash"></i>
                                                                    </button>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        className="qb-room-input"
                                                                        value={data.room_config[room.key]}
                                                                        onChange={e => handleRoomChange(room.key, e.target.value)}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="qb-room-btn"
                                                                        onClick={() => handleRoomChange(room.key, data.room_config[room.key] + 1)}
                                                                    >
                                                                        <i className="bi bi-plus"></i>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Pax validation */}
                                                    <div className="qb-rooms-summary mt-4">
                                                        <div className="qb-rooms-pax-compare">
                                                            <div className="qb-compare-item">
                                                                <span className="qb-compare-label">From Rooms</span>
                                                                <span className="qb-compare-value">
                                                                    {totalPaxFromRooms.adults}A + {totalPaxFromRooms.children}C + {totalPaxFromRooms.infants}I
                                                                </span>
                                                            </div>
                                                            <i className="bi bi-arrow-left-right text-muted"></i>
                                                            <div className="qb-compare-item">
                                                                <span className="qb-compare-label">Travelers</span>
                                                                <span className="qb-compare-value">
                                                                    {data.adults}A + {data.children}C + {data.infants}I
                                                                </span>
                                                            </div>
                                                            {(totalPaxFromRooms.adults !== data.adults ||
                                                              totalPaxFromRooms.children !== data.children ||
                                                              totalPaxFromRooms.infants !== data.infants) && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-warning btn-sm"
                                                                    onClick={syncPaxWithRooms}
                                                                >
                                                                    <i className="bi bi-arrow-repeat me-1"></i>
                                                                    Sync
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="qb-step-actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-lg"
                                                    onClick={() => setStep(2)}
                                                >
                                                    <i className="bi bi-arrow-left me-2"></i>
                                                    Back
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-lg"
                                                    onClick={() => setStep(4)}
                                                >
                                                    Next: Settings
                                                    <i className="bi bi-arrow-right ms-2"></i>
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* Step 4: Settings */}
                                    {step === 4 && (
                                        <motion.div
                                            key="step4"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            className="qb-step-content"
                                        >
                                            <div className="qb-card">
                                                <div className="qb-card-header">
                                                    <i className="bi bi-calculator"></i>
                                                    <h3>Markup Settings</h3>
                                                </div>
                                                <div className="qb-card-body">
                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <label className="qb-label">Markup Type</label>
                                                            <div className="qb-toggle-group">
                                                                <button
                                                                    type="button"
                                                                    className={`qb-toggle ${data.markup_type === 'percentage' ? 'active' : ''}`}
                                                                    onClick={() => setData('markup_type', 'percentage')}
                                                                >
                                                                    <i className="bi bi-percent"></i>
                                                                    Percentage
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`qb-toggle ${data.markup_type === 'fixed' ? 'active' : ''}`}
                                                                    onClick={() => setData('markup_type', 'fixed')}
                                                                >
                                                                    <i className="bi bi-currency-rupee"></i>
                                                                    Fixed Amount
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="qb-label">
                                                                Markup Value {data.markup_type === 'percentage' && '(%)'}
                                                            </label>
                                                            <div className="input-group input-group-lg">
                                                                {data.markup_type === 'fixed' && (
                                                                    <span className="input-group-text">{currency.symbol}</span>
                                                                )}
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0"
                                                                    className="form-control"
                                                                    value={data.markup_value}
                                                                    onChange={e => setData('markup_value', e.target.value)}
                                                                />
                                                                {data.markup_type === 'percentage' && (
                                                                    <span className="input-group-text">%</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="qb-card">
                                                <div className="qb-card-header">
                                                    <i className="bi bi-file-text"></i>
                                                    <h3>Terms & Policies</h3>
                                                    <span className="text-muted small">(Optional - can add later)</span>
                                                </div>
                                                <div className="qb-card-body">
                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <label className="qb-label">Inclusions</label>
                                                            <textarea
                                                                className="form-control"
                                                                rows="4"
                                                                value={data.inclusions}
                                                                onChange={e => setData('inclusions', e.target.value)}
                                                                placeholder="• Hotel accommodation&#10;• Daily breakfast&#10;• Airport transfers..."
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="qb-label">Exclusions</label>
                                                            <textarea
                                                                className="form-control"
                                                                rows="4"
                                                                value={data.exclusions}
                                                                onChange={e => setData('exclusions', e.target.value)}
                                                                placeholder="• Airfare&#10;• Visa charges&#10;• Personal expenses..."
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="qb-step-actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-lg"
                                                    onClick={() => setStep(3)}
                                                >
                                                    <i className="bi bi-arrow-left me-2"></i>
                                                    Back
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="btn btn-success btn-lg"
                                                    disabled={processing}
                                                >
                                                    {processing ? (
                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                    ) : (
                                                        <i className="bi bi-rocket-takeoff me-2"></i>
                                                    )}
                                                    Create & Start Building
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </form>
                        </div>

                        {/* Sidebar / Preview */}
                        <div className={showPreview ? 'col-lg-5' : 'col-lg-4'}>
                            <div className="qb-sidebar">
                                {/* Query Summary */}
                                <div className="qb-sidebar-card">
                                    <div className="qb-sidebar-header">
                                        <i className="bi bi-bookmark"></i>
                                        <h4>Query Details</h4>
                                    </div>
                                    <div className="qb-sidebar-body">
                                        <div className="qb-info-row">
                                            <span className="qb-info-label">Query ID</span>
                                            <span className="qb-info-value">{query.query_number}</span>
                                        </div>
                                        <div className="qb-info-row">
                                            <span className="qb-info-label">Client</span>
                                            <span className="qb-info-value">{query.client?.name || query.guest_name}</span>
                                        </div>
                                        {query.destinations?.length > 0 && (
                                            <div className="qb-info-row">
                                                <span className="qb-info-label">Destinations</span>
                                                <span className="qb-info-value">
                                                    {query.destinations.map(d => d.name).join(' → ')}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Live Summary */}
                                <div className="qb-sidebar-card qb-summary-card">
                                    <div className="qb-sidebar-header">
                                        <i className="bi bi-lightning"></i>
                                        <h4>Quotation Summary</h4>
                                    </div>
                                    <div className="qb-sidebar-body">
                                        <div className="qb-summary-item">
                                            <i className="bi bi-calendar3"></i>
                                            <div>
                                                <span className="qb-summary-label">Duration</span>
                                                <span className="qb-summary-value">
                                                    {data.nights || 0} Nights / {(data.nights || 0) + 1} Days
                                                </span>
                                            </div>
                                        </div>
                                        <div className="qb-summary-item">
                                            <i className="bi bi-people"></i>
                                            <div>
                                                <span className="qb-summary-label">Travelers</span>
                                                <span className="qb-summary-value">
                                                    {data.adults} Adults
                                                    {data.children > 0 && `, ${data.children} Children`}
                                                    {data.infants > 0 && `, ${data.infants} Infants`}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="qb-summary-item">
                                            <i className="bi bi-building"></i>
                                            <div>
                                                <span className="qb-summary-label">Rooms</span>
                                                <span className="qb-summary-value">
                                                    {Object.entries(data.room_config)
                                                        .filter(([_, v]) => v > 0)
                                                        .map(([k, v]) => `${v} ${k.toUpperCase()}`)
                                                        .join(', ') || 'Not configured'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="qb-summary-item">
                                            <i className="bi bi-percent"></i>
                                            <div>
                                                <span className="qb-summary-label">Markup</span>
                                                <span className="qb-summary-value">
                                                    {data.markup_type === 'percentage'
                                                        ? `${data.markup_value}%`
                                                        : `${currency.symbol}${data.markup_value}`}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Help */}
                                <div className="qb-sidebar-card qb-help-card">
                                    <div className="qb-help-icon">
                                        <i className="bi bi-lightbulb"></i>
                                    </div>
                                    <div className="qb-help-content">
                                        <h5>What's Next?</h5>
                                        <p>
                                            After creating the quotation, you'll be taken to the
                                            <strong> Itinerary Builder</strong> where you can add
                                            hotels, transfers, sightseeing, and more.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .qb-create-page {
                    min-height: 100vh;
                    background: #f8fafc;
                }

                .qb-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1.5rem 2rem;
                    background: #fff;
                    border-bottom: 1px solid #e2e8f0;
                }

                .qb-header-left {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .qb-back-btn {
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f1f5f9;
                    border-radius: 10px;
                    color: #64748b;
                    transition: all 0.2s;
                }

                .qb-back-btn:hover {
                    background: #e2e8f0;
                    color: #334155;
                }

                .qb-title {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                }

                .qb-subtitle {
                    font-size: 0.875rem;
                    color: #64748b;
                    margin: 0;
                }

                .qb-steps {
                    display: flex;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 1.5rem 2rem;
                    background: #fff;
                    border-bottom: 1px solid #e2e8f0;
                }

                .qb-step {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.75rem 1.5rem;
                    background: transparent;
                    border: 2px solid transparent;
                    border-radius: 50px;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .qb-step:hover {
                    background: #f1f5f9;
                }

                .qb-step.active {
                    background: #6366f1;
                    color: #fff;
                }

                .qb-step.completed {
                    color: #10b981;
                }

                .qb-step.completed .qb-step-number {
                    background: #10b981;
                    color: #fff;
                }

                .qb-step-number {
                    width: 28px;
                    height: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #e2e8f0;
                    border-radius: 50%;
                    font-weight: 600;
                    font-size: 0.8125rem;
                }

                .qb-step.active .qb-step-number {
                    background: rgba(255,255,255,0.2);
                }

                .qb-step-title {
                    font-weight: 500;
                    font-size: 0.9375rem;
                }

                .qb-content {
                    padding: 2rem;
                    max-width: 1400px;
                    margin: 0 auto;
                }

                .qb-card {
                    background: #fff;
                    border-radius: 16px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    margin-bottom: 1.5rem;
                    overflow: hidden;
                }

                .qb-card-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 1.25rem 1.5rem;
                    border-bottom: 1px solid #f1f5f9;
                }

                .qb-card-header i {
                    font-size: 1.25rem;
                    color: #6366f1;
                }

                .qb-card-header h3 {
                    font-size: 1.125rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0;
                    flex: 1;
                }

                .qb-card-badge {
                    font-size: 0.75rem;
                    padding: 0.25rem 0.75rem;
                    background: #f1f5f9;
                    border-radius: 50px;
                    color: #64748b;
                }

                .qb-card-body {
                    padding: 1.5rem;
                }

                .qb-label {
                    display: block;
                    font-weight: 500;
                    font-size: 0.875rem;
                    color: #475569;
                    margin-bottom: 0.5rem;
                }

                .qb-duration-display {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 1rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    justify-content: center;
                }

                .qb-duration-value {
                    text-align: center;
                }

                .qb-duration-number {
                    display: block;
                    font-size: 2rem;
                    font-weight: 700;
                    color: #6366f1;
                    line-height: 1;
                }

                .qb-duration-label {
                    font-size: 0.75rem;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                .qb-duration-separator {
                    font-size: 1.5rem;
                    color: #cbd5e1;
                }

                .qb-pax-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                }

                .qb-pax-item {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 1rem 1.25rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 2px solid transparent;
                    transition: all 0.2s;
                }

                .qb-pax-item:hover {
                    border-color: #e2e8f0;
                }

                .qb-pax-icon {
                    width: 48px;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 12px;
                    color: #fff;
                    font-size: 1.25rem;
                }

                .qb-pax-info {
                    flex: 1;
                }

                .qb-pax-label {
                    display: block;
                    font-weight: 600;
                    color: #1e293b;
                }

                .qb-pax-desc {
                    font-size: 0.75rem;
                    color: #64748b;
                }

                .qb-pax-controls {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .qb-pax-btn {
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.15s;
                }

                .qb-pax-btn:hover {
                    background: #6366f1;
                    border-color: #6366f1;
                    color: #fff;
                }

                .qb-pax-value {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1e293b;
                    min-width: 2rem;
                    text-align: center;
                }

                .qb-pax-summary {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-top: 1rem;
                    padding-top: 1rem;
                    border-top: 1px solid #e2e8f0;
                }

                .qb-pax-total {
                    color: #64748b;
                }

                .qb-rooms-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 1rem;
                }

                .qb-room-card {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 1.25rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 2px solid transparent;
                    transition: all 0.2s;
                }

                .qb-room-card:hover {
                    border-color: #e2e8f0;
                }

                .qb-room-card.active {
                    background: #f0f9ff;
                    border-color: #6366f1;
                }

                .qb-room-icon {
                    width: 44px;
                    height: 44px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 10px;
                    color: #fff;
                    font-size: 1.25rem;
                    margin-bottom: 0.75rem;
                }

                .qb-room-info {
                    text-align: center;
                    margin-bottom: 0.75rem;
                }

                .qb-room-label {
                    display: block;
                    font-weight: 600;
                    color: #1e293b;
                    font-size: 0.9375rem;
                }

                .qb-room-desc {
                    font-size: 0.75rem;
                    color: #64748b;
                }

                .qb-room-controls {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }

                .qb-room-btn {
                    width: 28px;
                    height: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.15s;
                    font-size: 0.875rem;
                }

                .qb-room-btn:hover {
                    background: #6366f1;
                    border-color: #6366f1;
                    color: #fff;
                }

                .qb-room-input {
                    width: 40px;
                    text-align: center;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    padding: 0.25rem;
                    font-weight: 600;
                    font-size: 0.9375rem;
                }

                .qb-room-input:focus {
                    outline: none;
                    border-color: #6366f1;
                }

                .qb-rooms-pax-compare {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 1.5rem;
                    padding: 1rem;
                    background: #f8fafc;
                    border-radius: 12px;
                }

                .qb-compare-item {
                    text-align: center;
                }

                .qb-compare-label {
                    display: block;
                    font-size: 0.75rem;
                    color: #64748b;
                    margin-bottom: 0.25rem;
                }

                .qb-compare-value {
                    font-weight: 600;
                    color: #1e293b;
                }

                .qb-toggle-group {
                    display: flex;
                    gap: 0.5rem;
                }

                .qb-toggle {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    background: #f8fafc;
                    border: 2px solid transparent;
                    border-radius: 10px;
                    cursor: pointer;
                    font-weight: 500;
                    transition: all 0.2s;
                }

                .qb-toggle:hover {
                    background: #f1f5f9;
                }

                .qb-toggle.active {
                    background: #6366f1;
                    color: #fff;
                }

                .qb-step-actions {
                    display: flex;
                    justify-content: space-between;
                    padding-top: 1rem;
                }

                /* Sidebar */
                .qb-sidebar {
                    position: sticky;
                    top: 1rem;
                }

                .qb-sidebar-card {
                    background: #fff;
                    border-radius: 16px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    margin-bottom: 1rem;
                    overflow: hidden;
                }

                .qb-sidebar-header {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 1rem 1.25rem;
                    border-bottom: 1px solid #f1f5f9;
                }

                .qb-sidebar-header i {
                    color: #6366f1;
                }

                .qb-sidebar-header h4 {
                    font-size: 0.9375rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0;
                }

                .qb-sidebar-body {
                    padding: 1rem 1.25rem;
                }

                .qb-info-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 0.5rem 0;
                    border-bottom: 1px dashed #f1f5f9;
                }

                .qb-info-row:last-child {
                    border-bottom: none;
                }

                .qb-info-label {
                    color: #64748b;
                    font-size: 0.8125rem;
                }

                .qb-info-value {
                    font-weight: 500;
                    color: #1e293b;
                    font-size: 0.8125rem;
                    text-align: right;
                }

                .qb-summary-card {
                    background: #6366f1;
                    color: #fff;
                }

                .qb-summary-card .qb-sidebar-header {
                    border-bottom-color: rgba(255,255,255,0.1);
                }

                .qb-summary-card .qb-sidebar-header i,
                .qb-summary-card .qb-sidebar-header h4 {
                    color: #fff;
                }

                .qb-summary-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                    padding: 0.75rem 0;
                    border-bottom: 1px solid rgba(255,255,255,0.1);
                }

                .qb-summary-item:last-child {
                    border-bottom: none;
                }

                .qb-summary-item > i {
                    font-size: 1.125rem;
                    opacity: 0.8;
                    margin-top: 0.125rem;
                }

                .qb-summary-label {
                    display: block;
                    font-size: 0.75rem;
                    opacity: 0.8;
                }

                .qb-summary-value {
                    font-weight: 500;
                    font-size: 0.875rem;
                }

                .qb-help-card {
                    display: flex;
                    gap: 1rem;
                    padding: 1.25rem;
                    background: #f0fdf4;
                    border: 1px solid #bbf7d0;
                }

                .qb-help-icon {
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #22c55e;
                    border-radius: 10px;
                    color: #fff;
                    font-size: 1.125rem;
                    flex-shrink: 0;
                }

                .qb-help-content h5 {
                    font-size: 0.9375rem;
                    font-weight: 600;
                    color: #166534;
                    margin: 0 0 0.5rem 0;
                }

                .qb-help-content p {
                    font-size: 0.8125rem;
                    color: #15803d;
                    margin: 0;
                    line-height: 1.5;
                }

                @media (max-width: 768px) {
                    .qb-header {
                        flex-direction: column;
                        gap: 1rem;
                        text-align: center;
                    }

                    .qb-steps {
                        flex-wrap: wrap;
                    }

                    .qb-step-title {
                        display: none;
                    }

                    .qb-rooms-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }
            `}</style>
        </AppLayout>
    );
}
